import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Skeleton, Empty, Button } from 'antd';
import ScrollReveal from '../../components/ScrollReveal';
import ajax from '../../utils/ajax';
import newsHeroBg from '../../assets/images/news/consulting-office-hero.png';
import cardBg01 from '../../assets/images/consulting/consulting-card-bg-01-building_compressed.webp';
import cardBg02 from '../../assets/images/consulting/consulting-card-bg-02-boxes_compressed.webp';
import cardBg03 from '../../assets/images/consulting/consulting-card-bg-03-charts_compressed.webp';
import cardBg04 from '../../assets/images/consulting/consulting-card-bg-04-globe_compressed.webp';
import cardBg05 from '../../assets/images/consulting/consulting-card-bg-05-mail_compressed.webp';

// 接口不返回图链时,按顺序轮询这 5 张原图作为兜底
const fallbackImages = [cardBg01, cardBg02, cardBg03, cardBg04, cardBg05];

const { Title } = Typography;

// 从接口返回里解析日期字符串
function parseDate(item) {
  // 1) 优先看有没有现成的 day/month
  if (item.day && item.month) {
    return { day: String(item.day), month: String(item.month) };
  }
  // 2) 否则从常见日期字段里解析
  const raw =
    item.date ||
    item.publishDate ||
    item.publish_date ||
    item.publishedAt ||
    item.createdAt ||
    item.createTime;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return { day, month };
    }
  }
  return { day: '--', month: '----' };
}

// 把接口的一条数据映射成卡片需要的字段 —— index 用于按序取兜底图
function normalizeItem(item, index = 0) {
  const { day, month } = parseDate(item);
  return {
    id: item.newsId,
    day,
    month,
    title: item.title,
    summary: item.summary || '',
    img: fallbackImages[index % fallbackImages.length],
    sourceUrl: item.sourceUrl,
  };
}

// 后端约定:code !== 200 即失败,列表在 rows 里 (Ruoyi 分页格式)
function checkBizCode(res) {
  if (res && typeof res === 'object' && 'code' in res) {
    return res.code === 200 || res.code === 0;
  }
  return true;
}

const News = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newsList, setNewsList] = useState([]);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ajax.get('/website/news');

      // 业务码判断 —— 后端约定 code !== 200 即失败
      if (!checkBizCode(res)) {
        setError(res.msg || '加载失败,请稍后再试');
        setNewsList([]);
        return;
      }

      const list = (res.rows || []).slice(0, 5).map((item, i) => normalizeItem(item, i));
      setNewsList(list);
    } catch (err) {
      setError(err.message || '资讯加载失败,请稍后再试');
      setNewsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  // 点击卡片:跳到独立详情路由 —— 真正的"单开一个页面"
  const openDetail = (item) => {
    if (item?.id != null) navigate(`/news/${item.id}`);
  };

  // 骨架屏 — 占 5 条
  const SkeletonList = (
    <div className="news-list">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="news-row-card news-row-card--skeleton">
          <div className="news-row-date">
            <Skeleton.Input
              active
              size="large"
              style={{ width: 60, marginBottom: 8 }}
            />
            <Skeleton.Input active size="small" style={{ width: 80 }} />
          </div>
          <div className="news-row-body" style={{ flex: 1 }}>
            <Skeleton.Input
              active
              size="default"
              style={{ width: '70%', marginBottom: 12 }}
            />
            <Skeleton.Node
              active
              style={{
                width: '100%',
                height: 14,
                marginBottom: 6,
                borderRadius: 4,
              }}
            >
              <span />
            </Skeleton.Node>
            <Skeleton.Node
              active
              style={{
                width: '85%',
                height: 14,
                borderRadius: 4,
              }}
            >
              <span />
            </Skeleton.Node>
          </div>
        </div>
      ))}
    </div>
  );

  // 列表渲染 — 错误或空都显示 Empty
  const ListBody = (() => {
    if (loading) return SkeletonList;

    if (error) {
      return (
        <Empty
          description={error}
          style={{ padding: '60px 0' }}
        >
          <Button type="primary" onClick={fetchNews}>
            重新加载
          </Button>
        </Empty>
      );
    }

    if (newsList.length === 0) {
      return <Empty description="暂无资讯" style={{ padding: '60px 0' }} />;
    }

    return (
      <ScrollReveal className="news-list" stagger={100}>
        {newsList.map((item) => (
          <div
            key={item.id}
            className="news-row-card"
            style={{ '--card-bg': item.img ? `url(${item.img})` : 'none' }}
            onClick={() => openDetail(item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openDetail(item);
              }
            }}
          >
            <div className="news-row-date">
              <div className="news-row-day">{item.day}</div>
              <div className="news-row-month">{item.month}</div>
            </div>
            <div className="news-row-body">
              <h3 className="news-row-title">{item.title}</h3>
              <p className="news-row-summary">{item.summary}</p>
            </div>
          </div>
        ))}
      </ScrollReveal>
    );
  })();

  return (
    <div className="page-news">
      {/* Hero — 与数据无关,直接渲染 */}
      <section
        className="news-hero"
        style={{ backgroundImage: `url(${newsHeroBg})` }}
      >
        <div className="news-hero-inner">
          <Title level={1} className="news-hero-title">
            财税资讯
          </Title>
        </div>
      </section>

      {/* 资讯动态 */}
      <section className="news-list-section">
        <div className="news-list-inner">
          <ScrollReveal>
            <div className="news-list-head">
              <Title level={2} className="news-list-title">
                资讯动态
              </Title>
              <span className="news-list-underline" />
            </div>
          </ScrollReveal>

          {ListBody}
        </div>
      </section>
    </div>
  );
};

export default News;